import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Award, HandHeart, Siren, ShieldCheck } from 'lucide-react';
import { socialService } from '../data/mock';

const SocialService = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-6xl font-bold text-white mb-6">Social Service & Humanitarian Work</h1>
            <p className="text-2xl text-white text-opacity-70 max-w-3xl mx-auto">
              Serving humanity through medical relief, disaster management, and community welfare
            </p>
          </motion.div>
        </div>
      </section>

      {/* Red Cross Leadership */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="p-12 bg-[#121212] border-2 border-[#00FFD1] mb-20"
          >
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-[#00FFD1] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="text-[#00FFD1]" size={40} />
              </div>
              <div className="flex-1">
                <h2 className="text-4xl font-bold text-white mb-4">District Hon'ble Secretary</h2>
                <p className="text-2xl text-[#00FFD1] mb-6">Indian Red Cross Society, Gadag</p>
                <p className="text-white text-opacity-85 text-lg leading-relaxed mb-6">
                  Leading humanitarian efforts across Gadag district with unwavering commitment. Under his tenure, 
                  the Gadag District Red Cross Society Branch received 2 appreciation awards from district administration 
                  and IMA Gadag for exceptional service.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-6 bg-black">
                    <p className="text-4xl font-bold text-[#00FFD1] mb-2">10K+</p>
                    <p className="text-white text-opacity-70">People Served</p>
                  </div>
                  <div className="p-6 bg-black">
                    <p className="text-4xl font-bold text-[#00FFD1] mb-2">50+</p>
                    <p className="text-white text-opacity-70">Relief Camps</p>
                  </div>
                  <div className="p-6 bg-black">
                    <p className="text-4xl font-bold text-[#00FFD1] mb-2">3</p>
                    <p className="text-white text-opacity-70">Major Disasters</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Service Areas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {socialService.map((service, index) => {
              const icons = [Heart, Siren, Users, HandHeart, Award, ShieldCheck];
              const Icon = icons[index % icons.length];
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="p-8 bg-[#121212] border border-white border-opacity-25"
                >
                  <div className="w-16 h-16 bg-[#00FFD1] bg-opacity-10 flex items-center justify-center mb-6">
                    <Icon className="text-[#00FFD1]" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                  <p className="text-white text-opacity-70 leading-relaxed mb-4">{service.description}</p>
                  <div className="pt-4 border-t border-white border-opacity-25">
                    <p className="text-[#00FFD1] font-medium">{service.impact}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Disaster Relief Work */}
      <section className="py-20 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-5xl font-bold text-white mb-16 text-center"
          >
            Major Disaster Relief Operations
          </motion.h2>

          <div className="space-y-8">
            {[
              {
                year: "2020-2021",
                title: "COVID-19 Pandemic Response",
                location: "Gadag District, Karnataka",
                description: "Led comprehensive pandemic response including distribution of 10,000+ masks, sanitizers, soaps, and food kits. Organized juice distribution for corona warriors and awareness campaigns."
              },
              {
                year: "2019",
                title: "Gujarat Flood Relief",
                location: "Vadodara, Gujarat",
                description: "Medical camp serving 135+ flood-affected beneficiaries with free medical treatment and essential medicines distribution in collaboration with Parul University."
              },
              {
                year: "2018",
                title: "Karnataka Flood Relief",
                location: "Madikeri, Kodagu",
                description: "Comprehensive medical services for flood victims including health check-ups, treatment, and distribution of essentials to disaster-affected families."
              },
              {
                year: "2018",
                title: "Jammu & Kashmir Flood Relief",
                location: "Anantnag, J&K",
                description: "Medical camp at ITBP Mahidanda providing health services and first aid training to flood-affected communities and jawans."
              }
            ].map((relief, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="p-8 bg-black border border-white border-opacity-25"
              >
                <div className="flex items-start gap-6">
                  <div className="w-24 flex-shrink-0">
                    <div className="w-24 h-24 bg-[#00FFD1] bg-opacity-10 border border-[#00FFD1] flex items-center justify-center">
                      <span className="text-xl font-bold text-[#00FFD1] text-center leading-tight">{relief.year}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold text-white mb-2">{relief.title}</h3>
                    <p className="text-[#00FFD1] text-lg mb-4">{relief.location}</p>
                    <p className="text-white text-opacity-85 leading-relaxed">{relief.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-32">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="p-12 bg-[#121212] border border-[#00FFD1] text-center"
          >
            <Heart className="text-[#00FFD1] mx-auto mb-6" size={48} />
            <p className="text-3xl text-white text-opacity-85 leading-relaxed mb-6">
              "Despite the undeniable challenges, his contribution in Flood Relief Camp organization and Disaster Management is commendable. 
              He serves the grief-stricken with resolve and commitment during natural calamities and disasters."
            </p>
            <div className="h-1 w-24 bg-[#00FFD1] mx-auto" />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SocialService;